import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Entete } from '../components/Entete'
import { MarqueHomonyme } from '../components/MarqueHomonyme'
import { useSession } from '../auth/session'
import { formaterDateFr } from '../lib/dates'
import { rechercherDossiers } from '../lib/dossiers'
import { TAILLE_PAGE, intervalleAffiche, nombrePages } from '../lib/pagination'
import { DELAI_FRAPPE_MS, preparerTerme } from '../lib/recherche'

/**
 * Liste des dossiers.
 *
 * Le tri, le filtre des archives et l'echappement du terme vivent dans
 * `public.rechercher_dossiers` : cette page n'assemble aucune requete.
 */
export default function ListeDossiers() {
  const etat = useSession()

  // La recherche et la page vivent dans l'URL, pas dans l'etat local : c'est ce
  // qui permet a la page d'un dossier de rendre exactement la liste d'ou l'on
  // vient. Un conseiller qui consulte trois dossiers d'affilee ne retape pas sa
  // recherche trois fois.
  const [parametres, setParametres] = useSearchParams()
  const terme = parametres.get('q') ?? ''
  const page = Math.max(1, Number(parametres.get('page') ?? '1') || 1)

  const [saisie, setSaisie] = useState(terme)

  // Declenchement differe apres la derniere frappe. Ce useEffect ne parle pas a
  // Supabase : il ne fait que retarder l'ecriture dans l'URL, et c'est
  // react-query qui decide ensuite s'il faut requeter.
  useEffect(() => {
    const minuterie = setTimeout(() => {
      if (saisie === terme) return
      const suivants = new URLSearchParams()
      if (preparerTerme(saisie) !== '') suivants.set('q', saisie)
      setParametres(suivants, { replace: true })
    }, DELAI_FRAPPE_MS)
    return () => clearTimeout(minuterie)
  }, [saisie, terme, setParametres])

  const termePrepare = preparerTerme(terme)

  function allerALaPage(numero: number): void {
    const suivants = new URLSearchParams(parametres)
    if (numero <= 1) suivants.delete('page')
    else suivants.set('page', String(numero))
    setParametres(suivants)
  }

  const requete = useQuery({
    queryKey: ['dossiers', termePrepare, page],
    queryFn: () => rechercherDossiers(termePrepare, page),
    placeholderData: keepPreviousData,
  })

  if (etat.statut !== 'connecte') return null

  const total = requete.data?.total ?? 0
  const pages = nombrePages(total, TAILLE_PAGE)
  const intervalle = intervalleAffiche(page, total, TAILLE_PAGE)

  return (
    <div className="min-h-screen bg-slate-50">
      <Entete profil={etat.profil} />

      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-xl font-semibold text-slate-900">Dossiers</h1>

        <label htmlFor="recherche" className="mt-6 block text-sm font-medium text-slate-700">
          Rechercher un apprenant
        </label>
        <input
          id="recherche"
          type="search"
          data-test="champ-recherche"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Nom ou prénom"
          className="mt-1 w-full max-w-md rounded border border-slate-300 px-3 py-2 focus:border-marque-primaire-texte focus:outline-none focus:ring-2 focus:ring-marque-primaire-texte"
        />

        <div className="mt-6">
          {/* Trois etats distincts, jamais confondus : une panne ne doit pas
              ressembler a une base vide. */}
          {requete.isPending ? (
            <p data-test="etat-chargement" role="status" className="text-slate-600">
              Chargement des dossiers…
            </p>
          ) : requete.isError ? (
            <div
              data-test="etat-erreur"
              role="alert"
              className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800"
            >
              <p className="font-medium">La requête a échoué.</p>
              <p className="mt-1">
                Les dossiers n’ont pas pu être chargés. Ce n’est pas une liste vide : réessayez, et
                prévenez un administrateur si cela persiste.
              </p>
              <button
                type="button"
                onClick={() => void requete.refetch()}
                className="mt-3 rounded border border-red-300 px-3 py-1 font-medium text-red-800 hover:bg-red-100"
              >
                Réessayer
              </button>
            </div>
          ) : total === 0 ? (
            <p data-test="etat-vide" className="text-slate-600">
              Aucun dossier ne correspond à cette recherche.
            </p>
          ) : (
            <>
              <table data-test="tableau-dossiers" className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-300 text-slate-600">
                    <th scope="col" className="py-2 pr-4 font-medium">Nom</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Prénom</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Date de création</th>
                    <th scope="col" className="py-2 font-medium">
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {requete.data.lignes.map((ligne) => (
                    <tr key={ligne.id} data-test="ligne-dossier" className="border-b border-slate-200">
                      <td className="py-2 pr-4 font-medium text-slate-900">{ligne.nom}
                        <MarqueHomonyme homonyme={ligne.homonyme} />
                      </td>
                      <td className="py-2 pr-4 text-slate-700">{ligne.prenom}</td>
                      <td className="py-2 pr-4 text-slate-700">{formaterDateFr(ligne.cree_le)}</td>
                      <td className="py-2 text-right">
                        <Link
                          to={`/dossier/${ligne.id}${parametres.toString() === '' ? '' : `?${parametres.toString()}`}`}
                          data-test="ouvrir-dossier"
                          className="font-medium text-marque-primaire-texte hover:text-marque-primaire-survol"
                        >
                          Ouvrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <span data-test="intervalle">
                  {intervalle.premier} à {intervalle.dernier} sur {total}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    data-test="page-precedente"
                    disabled={page <= 1}
                    onClick={() => allerALaPage(page - 1)}
                    className="rounded border border-slate-300 px-3 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <span data-test="page-courante">
                    Page {page} sur {pages}
                  </span>
                  <button
                    type="button"
                    data-test="page-suivante"
                    disabled={page >= pages}
                    onClick={() => allerALaPage(Math.min(pages, page + 1))}
                    className="rounded border border-slate-300 px-3 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
